import type { CharacterId } from '../../../../domain/ids/ids';
import type { BoosterKind } from '../../../../domain/catalog/boosters';
import claudiBase from '../../components/atomic/character/characters_image/claudi.png';
import eddyBase from '../../components/atomic/character/characters_image/eddy.png';
import rockBase from '../../components/atomic/character/characters_image/rock.png';
import tuchiBase from '../../components/atomic/character/characters_image/tuchi.png';
import ufoBase from '../../components/atomic/character/characters_image/ufo.png';
import vallBase from '../../components/atomic/character/characters_image/vall.png';
import claudiSpeed from '../../components/atomic/character/speed_booster_character_images/claudi.png';
import eddySpeed from '../../components/atomic/character/speed_booster_character_images/eddy.png';
import rockSpeed from '../../components/atomic/character/speed_booster_character_images/rock.png';
import tuchiSpeed from '../../components/atomic/character/speed_booster_character_images/tuchi.png';
import ufoSpeed from '../../components/atomic/character/speed_booster_character_images/ufo.png';
import vallSpeed from '../../components/atomic/character/speed_booster_character_images/vall.png';
import shieldShine from '../../components/atomic/character/shield_booster_shine/shield_booster_shine.png';

export type LevelCharacterSpriteVariant = 'normal' | BoosterKind | 'shield';

const BASE_BY_ID: Record<CharacterId, string> = {
  claudi: claudiBase,
  eddy: eddyBase,
  rock: rockBase,
  tuchi: tuchiBase,
  ufo: ufoBase,
  vall: vallBase,
};

const SPEED_BY_ID: Record<CharacterId, string> = {
  claudi: claudiSpeed,
  eddy: eddySpeed,
  rock: rockSpeed,
  tuchi: tuchiSpeed,
  ufo: ufoSpeed,
  vall: vallSpeed,
};

export type LevelCharacterSprite = {
  characterSrc: string;
  showShieldShine: boolean;
  shieldShineSrc: string;
};

/** Intrinsic PNG canvas sizes (must match the files on disk). */
type SpriteIntrinsicSize = { width: number; height: number };

const BASE_SIZE_BY_ID: Record<CharacterId, SpriteIntrinsicSize> = {
  claudi: { width: 255, height: 220 },
  eddy: { width: 217, height: 161 },
  rock: { width: 229, height: 415 },
  tuchi: { width: 152, height: 275 },
  ufo: { width: 240, height: 254 },
  vall: { width: 394, height: 258 },
};

const SPEED_SIZE_BY_ID: Record<CharacterId, SpriteIntrinsicSize> = {
  claudi: { width: 422, height: 259 },
  eddy: { width: 415, height: 219 },
  rock: { width: 245, height: 397 },
  tuchi: { width: 374, height: 299 },
  ufo: { width: 336, height: 286 },
  vall: { width: 356, height: 283 },
};

/**
 * Opaque content AABB to match across normal ↔ speed (alpha > ~8).
 * For speed art this is the character *core* (body / jetpack / package) —
 * near-white motion smoke/trails are excluded so VFX can extend without
 * shrinking or inflating the readable character size (esp. Claudi).
 */
const BASE_CONTENT_SIZE_BY_ID: Record<CharacterId, SpriteIntrinsicSize> = {
  claudi: { width: 255, height: 220 },
  eddy: { width: 217, height: 161 },
  rock: { width: 228, height: 414 },
  tuchi: { width: 152, height: 261 },
  ufo: { width: 236, height: 250 },
  vall: { width: 394, height: 258 },
};

/** Speed-art core AABB (smoke/trail excluded). */
const SPEED_CONTENT_SIZE_BY_ID: Record<CharacterId, SpriteIntrinsicSize> = {
  claudi: { width: 258, height: 250 },
  eddy: { width: 253, height: 201 },
  rock: { width: 237, height: 388 },
  tuchi: { width: 194, height: 261 },
  ufo: { width: 235, height: 282 },
  vall: { width: 355, height: 258 },
};

export function getAllLevelCharacterSpriteUrls(): string[] {
  const urls = [shieldShine];
  for (const id of Object.keys(BASE_BY_ID) as CharacterId[]) {
    urls.push(BASE_BY_ID[id], SPEED_BY_ID[id]);
  }
  return urls;
}

/**
 * Display-box scale relative to the normal player sprite frame.
 * Hitbox stays on RUNNER_PLAYER_SPRITE_* — only the drawn img container uses this.
 *
 * For speed art:
 * 1) canvas ratio equalizes PNG pixel density under object-fit:contain;
 * 2) core content-bounds fit keeps the character body the same on-screen size;
 *    smoke/trails may extend past the normal silhouette.
 */
export function getLevelCharacterSpriteDisplayScale(
  characterId: CharacterId,
  variant: LevelCharacterSpriteVariant,
): { scaleX: number; scaleY: number } {
  if (variant !== 'speed') {
    return { scaleX: 1, scaleY: 1 };
  }
  const base = BASE_SIZE_BY_ID[characterId];
  const speed = SPEED_SIZE_BY_ID[characterId];
  const baseContent = BASE_CONTENT_SIZE_BY_ID[characterId];
  const speedContent = SPEED_CONTENT_SIZE_BY_ID[characterId];
  const contentFit = Math.min(
    baseContent.width / speedContent.width,
    baseContent.height / speedContent.height,
  );
  return {
    scaleX: (speed.width / base.width) * contentFit,
    scaleY: (speed.height / base.height) * contentFit,
  };
}

/** Visual nudge inside the player sprite frame (% of frame size). Hitbox stays centered. */
export type LevelCharacterSpriteDisplayOffset = { xPct: number; yPct: number };

const DISPLAY_OFFSET_BY_ID: Partial<Record<CharacterId, LevelCharacterSpriteDisplayOffset>> = {
  // UFO art is top/right-heavy in the PNG — nudge left & down to sit in the hitbox.
  ufo: { xPct: -8, yPct: 20 },
};

export function getLevelCharacterSpriteDisplayOffset(
  characterId: CharacterId,
): LevelCharacterSpriteDisplayOffset {
  return DISPLAY_OFFSET_BY_ID[characterId] ?? { xPct: 0, yPct: 0 };
}

export function getLevelCharacterSprite(
  characterId: CharacterId,
  variant: LevelCharacterSpriteVariant,
): LevelCharacterSprite {
  if (variant === 'speed') {
    return {
      characterSrc: SPEED_BY_ID[characterId],
      showShieldShine: false,
      shieldShineSrc: shieldShine,
    };
  }
  return {
    characterSrc: BASE_BY_ID[characterId],
    showShieldShine: variant === 'shield',
    shieldShineSrc: shieldShine,
  };
}
